import moment from "moment";

// Who actually performed the triggering action — a member acting on their own behalf through
// the User App, vs. a coach/admin acting through the Office App — as opposed to which app is
// currently displaying the notification (both apps show both sources). Backed by the existing
// `created_by_type` column the API already returns (see api/src/services/notification.service.ts);
// "system"/null (no human actor, e.g. an automated job) falls back to "office-app" since those
// are operationally staff-facing, not member-initiated.
function sourceFromCreator(n) {
  return n.created_by_type === "client" ? "user-app" : "office-app";
}

// The Notifications screen's "Tip" filter (Paketi / Zakazivanja / Ostalo) groups by WHAT
// happened, a different axis than `source` above (WHO triggered it) — a member's own package
// request and a coach assigning one both belong under "package" regardless of source. Kept as
// its own small lookup (not folded into the big switch below) so the category list stays easy
// to audit against the filter's three buckets.
const CATEGORY_BY_TYPE = {
  package_requested: "package",
  package_created: "package",
  package_updated: "package",
  package_assigned: "package",
  booking_created: "booking",
  booking_canceled: "booking",
  booking_rescheduled: "booking",
  booking_noshow: "booking",
};
function categoryFromType(type) {
  return CATEGORY_BY_TYPE[type] || "other";
}

// Short bold heading shown above the existing full-sentence message — lets a trainer tell a
// package request apart from a booking at a glance without reading the sentence, per type.
const CATEGORY_LABEL_BY_TYPE = {
  package_requested: "Zahtev za paket",
  package_created: "Novi paket",
  package_updated: "Izmena paketa",
  package_assigned: "Paket dodeljen",
  booking_created: "Novo zakazivanje",
  booking_canceled: "Otkazivanje termina",
  booking_rescheduled: "Termin premešten",
  booking_noshow: "Odsutan sa treninga",
  client_registered: "Nova registracija",
  client_verified_email: "Verifikacija",
  client_verified_phone: "Verifikacija",
  profile_updated: "Izmena profila",
};

// Maps a raw API notification (type + whatever entity data GET /notifications joined in) to
// the {id, type, category, categoryLabel, source, message, timestamp, read, route} shape
// NotificationsDrawer.jsx already renders — the API deliberately stores no pre-rendered
// text/route (see the notifications migration), so each app decides its own copy/routing here,
// per `type`. `category`/`categoryLabel` are purely additive display fields (no change to
// `message`/`route`/anything else below) so this stays a frontend-only enhancement.
export function toDisplayNotification(n) {
  const clientName =
    `${n.client_first_name || ""} ${n.client_last_name || ""}`.trim() || "Klijent";
  const timeslotWhen = n.timeslot_start
    ? moment(n.timeslot_start).format("dddd, DD.MM.YYYY [u] HH:mm")
    : "";

  const base = {
    id: n.id,
    timestamp: n.created_at,
    read: n.read,
    source: sourceFromCreator(n),
    category: categoryFromType(n.type),
    categoryLabel: CATEGORY_LABEL_BY_TYPE[n.type] || "Obaveštenje",
  };

  switch (n.type) {
    case "client_registered":
      return { ...base, type: "register", message: `${clientName} se registrovao/la.`, route: "/members" };
    case "client_verified_email":
      return { ...base, type: "verified", message: `${clientName} je verifikovao/la email.`, route: "/members" };
    case "client_verified_phone":
      return { ...base, type: "verified", message: `${clientName} je verifikovao/la broj telefona.`, route: "/members" };
    case "profile_updated":
      return { ...base, type: "profile", message: `${clientName} je ažurirao/la svoj profil.`, route: "/members" };
    case "booking_created":
      return {
        ...base,
        type: "booking",
        message: `${clientName} je zakazao/la termin za ${timeslotWhen}.`,
        route: "/",
      };
    case "booking_canceled":
      return {
        ...base,
        type: "cancel",
        message: `${clientName} je otkazao/la termin za ${timeslotWhen}.`,
        route: "/",
      };
    case "package_assigned":
      return {
        ...base,
        type: "package",
        message: `${clientName} je dobio/la paket "${n.package_name || ""}".`,
        route: "/members",
      };
    case "package_updated":
      return {
        ...base,
        type: "package",
        message: `Paket klijenta ${clientName} je izmenjen.`,
        route: "/members",
      };
    case "package_requested":
      // reviewClientId tells Members/index.js to auto-open this client's package review
      // (Odobri paket / Obriši paket) right after navigating here, instead of making staff
      // find them in the list themselves.
      return {
        ...base,
        type: "package",
        message: `${clientName} je zatražio/la paket "${n.package_name || ""}".`,
        route: `/members?reviewClientId=${n.client_id}`,
      };
    default:
      return { ...base, type: "profile", message: `Novo obaveštenje za ${clientName}.`, route: "/members" };
  }
}

// "pre 2 min" / "pre 3 sata" / "pre 2 dana" / falls back to a plain date past a week.
export function formatRelativeTime(iso) {
  const diffMs = Date.now() - new Date(iso).getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "upravo sada";
  if (diffMin < 60) return `pre ${diffMin} min`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `pre ${diffH} ${diffH === 1 ? "sat" : diffH < 5 ? "sata" : "sati"}`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 7) return `pre ${diffD} ${diffD === 1 ? "dan" : "dana"}`;
  const d = new Date(iso);
  return `${d.getDate()}.${d.getMonth() + 1}.${d.getFullYear()}.`;
}
